import React, { useEffect, useRef } from 'react';
import { getEffective, onAutoLoadChange, onOverrideChange } from '@paxsudos/upload-by-url/auto_load_prefs';
import { uplinkAttachWithProgress } from '@paxsudos/upload-by-url/uplink_attach';

const DEBUG = true;
const log = (...a) => { if (DEBUG) console.info('[upload-by-url]', ...a); };

// Yksi prosessitason tila React mount/unmount -syklien yli.
const globalState = window.__PAX_UPLOAD_BY_URL_STATE ||= {
  ownerMounted: false,
  recentlyAttached: new Map(),
  inflight: new Set(),
};

// tekstikenttä
const TA_SELECTOR = [
  'textarea.autosuggest-textarea__textarea',
  '.autosuggest-textarea__textarea',
  'div[contenteditable="true"].autosuggest-textarea__textarea',
].join(',');

// drop-kohteet (järjestys tärkeä)
const DROPZONE_CANDIDATES = [
  '.compose-form__upload-area',
  '.compose-form .compose-form__upload-area',
  '.compose-form__dropzone',
  '.compose-form .compose-form__dropzone',
  'form.compose-form',
  '.compose-form',
];

// file input fallback
const FILE_INPUT_CANDIDATES = [
  'form.compose-form input[type="file"]',
  '.compose-form input[type="file"]',
];

// liitteiden laskenta – useita mahdollisia luokkia
const ATTACHMENT_COUNTERS = [
  '.compose-form__uploads .compose-form__upload',
  '.compose-form .compose-form__upload',
  '.compose-form .media-gallery__item',
  '.compose-form__attachments .media-gallery__item',
];

const urlRegex = /(https?:\/\/[^\s<>"'()]+)(?=\s|$)/gi;


// Normalisoi URL (proto pieneksi, poista turhat trailing-slashet, säilytä query/hash)
const normalizeUrl = (raw) => {
  if (!raw) return '';
  try {
    const s = String(raw).trim();
    const u = new URL(s);
    u.protocol = u.protocol.toLowerCase();
    if (u.pathname.endsWith('/') && u.pathname !== '/') {
      u.pathname = u.pathname.replace(/\/+$/, '');
    }
    return u.toString();
  } catch {
    return String(raw).trim();
  }
};
const COOLDOWN_MS = 800;
const RECENT_TTL  = 1200; // “tuplapurskeet” parin framen ajan

const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

// TTL-välimuisti aidosti globaalisti mounttien yli.
const touchRecent = (u) => globalState.recentlyAttached.set(u, Date.now() + RECENT_TTL);
const isRecent = (u) => (globalState.recentlyAttached.get(u) || 0) > Date.now();

async function probe(url) {
  const r = await fetch(`/upload-by-url/probe?url=${encodeURIComponent(url)}`, { credentials: 'include' });
  if (!r.ok) return { ok: true, ok_for_upload: false, status: r.status };
  return r.json();
}

async function uplink(url) {
  const r = await fetch('/upload-by-url/uplink', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'x-csrf-token': document.querySelector('meta[name="csrf-token"]')?.content || '',
    },
    body: JSON.stringify({ url }),
  });
  const json = await r.json();
  if (!r.ok || !json?.media) throw new Error(json?.error || 'uplink failed');
  return json.media; // { id, preview_url, type, ... }
}


async function fetchData(url) {
  const r = await fetch('/upload-by-url', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!r.ok) throw new Error(`upload-by-url POST ${r.status}`);
  return r.json();
}

function dataURLToFile(dataUrl, filename, mime) {
  const i = dataUrl.indexOf(',');
  const b64 = dataUrl.slice(i + 1);
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let k = 0; k < bin.length; k++) arr[k] = bin.charCodeAt(k);
  return new File([arr], filename || 'media', { type: mime || '' });
}

function resolveComposerRoot(el) {
  return el.closest?.('form.compose-form, .compose-form, form') ||
         document.querySelector('form.compose-form, .compose-form') ||
         document.body;
}

function queryOne(root, sels) {
  for (const s of sels) {
    const el = root.querySelector?.(s);
    if (el) return el;
  }
  return null;
}

function countAttachments(root) {
  for (const s of ATTACHMENT_COUNTERS) {
    const n = root.querySelectorAll?.(s)?.length || 0;
    if (n > 0) return n;
  }
  // fallback: file input files
  const inp = queryOne(root, FILE_INPUT_CANDIDATES);
  if (inp && inp.files) return inp.files.length;
  return 0;
}

function getText(el) {
  if (!el) return '';
  if (el.tagName === 'TEXTAREA') return el.value || '';
  if (el.getAttribute && el.getAttribute('contenteditable') === 'true') return el.textContent || '';
  return el.value || el.textContent || '';
}
function setText(el, text) {
  if (!el) return;
  if (el.tagName === 'TEXTAREA') { el.value = text; }
  else if (el.getAttribute && el.getAttribute('contenteditable') === 'true') { el.textContent = text; }
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function tryDropFile(target, file) {
  try {
    if (!('DataTransfer' in window)) return false;
    const dt = new DataTransfer();
    dt.items.add(file);

    for (const type of ['dragenter', 'dragover']) {
      let ev;
      try { ev = new DragEvent(type, { bubbles: true, cancelable: true, dataTransfer: dt }); }
      catch { ev = new Event(type, { bubbles: true, cancelable: true }); Object.defineProperty(ev, 'dataTransfer', { value: dt }); }
      target.dispatchEvent(ev);
    }
    let dropEv;
    try { dropEv = new DragEvent('drop', { bubbles: true, cancelable: true, dataTransfer: dt }); }
    catch { dropEv = new Event('drop', { bubbles: true, cancelable: true }); Object.defineProperty(dropEv, 'dataTransfer', { value: dt }); }
    target.dispatchEvent(dropEv);

    return true; // todellinen varmistus tehdään laskemalla liitteet
  } catch (e) {
    log('tryDropFile failed', e);
    return false;
  }
}

function trySetFileInput(input, file) {
  try {
    if (!('DataTransfer' in window)) return false;
    const dt = new DataTransfer();
    if (input.files && input.files.length) Array.from(input.files).forEach(f => dt.items.add(f));
    dt.items.add(file);
    input.files = dt.files; // voi olla read-only – kokeillaan
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return !!(input.files && input.files.length);
  } catch (e) {
    log('trySetFileInput failed', e);
    return false;
  }
}

export default function AutoAttachByUrl() {
  // per-form tila
  const perFormRef = useRef(new WeakMap());
  const enabledRef = useRef(getEffective());

  useEffect(() => {
    if (globalState.ownerMounted) { log('already mounted → skip'); return undefined; }
    globalState.ownerMounted = true;

    log('auto-attach boot, enabled=', enabledRef.current);

    const unsub1 = onAutoLoadChange?.((v) => { enabledRef.current = v; log('autoLoadChange ->', v); });
    const unsub2 = onOverrideChange?.((v) => { enabledRef.current = v; log('overrideChange ->', v); });

    const getFormState = (formEl) => {
      let st = perFormRef.current.get(formEl);
      if (!st) {
        st = { seen: new Set(), inflight: new Set(), attached: new Set(), lastTextHash: '', cooldownUntil: 0 };
        perFormRef.current.set(formEl, st);
        if (formEl && !formEl.dataset.paxSeenWired) {
          // reset kun lähetetään tai kun liitteet poistuvat kaikki
          formEl.addEventListener('submit', () => { perFormRef.current.delete(formEl); log('form submit → reset'); });
          formEl.dataset.paxSeenWired = '1';
        }
        // havainnoi liitealue – jos tyhjenee, nollaa seen/attached → sallii saman URLin heti
        const comp = formEl.closest?.('.compose-form') || formEl;
        const mo = new MutationObserver(() => {
          const n = countAttachments(comp);
          if (n === 0) {
            st.seen.clear();
            st.attached.clear();
            log('attachments empty → clear form state');
          }
        });
        mo.observe(comp, { childList: true, subtree: true });
        st._mo = mo;
      }
      return st;
    };

    const wait = (ms) => new Promise(r => setTimeout(r, ms));

    const processTarget = debounce(async (target) => {
      if (!enabledRef.current || !target) return;

      const composer = resolveComposerRoot(target);
      const formEl = composer.closest?.('form') || composer;
      const state = getFormState(formEl);

      if (Date.now() < state.cooldownUntil) return;

      const txt = getText(target);
      const textHash = txt;
      if (!txt || !/\S/.test(txt)) {
        state.seen.clear(); state.inflight.clear(); state.attached.clear(); state.lastTextHash = '';
        return;
      }
      if (state.lastTextHash === textHash && !/https?:\/\//i.test(txt)) return; // ei uutta
      state.lastTextHash = textHash;

      const matches = [...txt.matchAll(urlRegex)].map(m => normalizeUrl(m[1])).filter(Boolean);
      if (!matches.length) return;

      const dropTarget = queryOne(composer, DROPZONE_CANDIDATES) || composer;
      const fileInput  = queryOne(composer, FILE_INPUT_CANDIDATES);

      log('found urls:', matches, 'dropTarget?', !!dropTarget, 'fileInput?', !!fileInput);

      const beforeCount = countAttachments(composer);

      for (const uRaw of matches) {
        
        const u = normalizeUrl(uRaw);
        if (isRecent(u)) continue;
        if (globalState.inflight.has(u) || state.inflight.has(u)) continue;
        if (state.seen.has(u) || state.attached.has(u)) continue;

        // Globaali lukko otetaan ennen ensimmäistä async-kutsua.
        globalState.inflight.add(u);
        state.inflight.add(u);
        try {
          const pr = await probe(u);
          if (!pr.ok_for_upload) continue;

          // Kaikki hyväksytyt URL-mediat kulkevat saman backend -> Mastodon
          // -> Redux -polun kautta. Ei synteettistä DOM drop/fileInput -kikkailua.
          try {
            await uplinkAttachWithProgress(u);

            const cleaned = getText(target)
              .replaceAll(uRaw, '')
              .replace(/\s{2,}/g, ' ')
              .trim();
            setText(target, cleaned);

            state.seen.add(u);
            state.attached.add(u);
            touchRecent(u);
            state.cooldownUntil = Date.now() + COOLDOWN_MS;
            log('uplink attached OK for', u);
          } catch (e) {
            log('uplink failed', e);
          }
        } catch (e) {
          log('failed for', u, e);
        } finally {
          state.inflight.delete(u);
          globalState.inflight.delete(u);
        }
      }
    }, 500);

    // vain INPUT – paste kuitenkin synnyttää inputin
    const onInput = (ev) => {
      const t = ev.target;
      if (!t || !(t.matches?.(TA_SELECTOR))) return;
      processTarget(t);
    };
    document.addEventListener('input', onInput, true);

    // komposerin ilmestyminen näkyville
    const mo = new MutationObserver(() => {
      const ta = document.querySelector(TA_SELECTOR);
      if (ta && !ta.dataset.paxBound) { ta.dataset.paxBound = '1'; log('composer ready'); }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // eka tarkistus pienen viiveen jälkeen
    setTimeout(() => {
      const ta = document.querySelector(TA_SELECTOR);
      if (ta) processTarget(ta);
    }, 150);

    return () => {
      mo.disconnect();
      document.removeEventListener('input', onInput, true);
      // sulje mahdolliset form-kohtaiset MO:t
      perFormRef.current && perFormRef.current.forEach?.(st => st._mo?.disconnect?.());
      try { unsub1 && unsub1(); unsub2 && unsub2(); } catch {}
      globalState.ownerMounted = false;
    };
  }, []);

  return null;
}
